import { useMemo } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { GridColDef } from '@mui/x-data-grid';
import Chip from '@mui/material/Chip';
import { Box, Stack, styled, Theme, Typography, useTheme } from '@mui/material';
import { ShiftTypePresetListItem } from '@libs/models/shiftTemplates';
import dayjs from 'dayjs';
import { getShiftType } from '@libs/models/shifts/shift';
import { FaIcon } from '@atoms/icons';

type TableColumnsProps = {
  localizationSubset: string;
};
export function useShiftTypePresetsListColumns({ localizationSubset }: TableColumnsProps) {
  const intl = useIntl();
  const theme = useTheme();
  // Columns should be memoized to prevent table flickering and state loss
  return useMemo(() => {
    const columns: GridColDef<ShiftTypePresetListItem>[] = [
      getNameColumn(intl, localizationSubset),
      getStartTimeColumn(intl, localizationSubset),
      getTypeColumn(intl, localizationSubset, theme),
      getDurationColumn(intl, localizationSubset),
    ];
    return columns;
  }, [intl]);
}

function getNameColumn(intl: IntlShape, localizationSubset: string): GridColDef<ShiftTypePresetListItem> {
  return {
    field: 'name',
    headerName: intl.formatMessage({ id: `${localizationSubset}.table.fields.name.heading` }),
    minWidth: 160,
    resizable: true,
    display: 'flex',
    flex: 1,
    sortable: true,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      if (!params.value) {
        return null;
      }
      return (
        <Stack
          direction={'column'}
          sx={{ height: '100%', overflowX: 'hidden', textOverflow: 'ellipsis' }}
          justifyContent={'center'}
          alignContent={'center'}
        >
          {params.value}
        </Stack>
      );
    },
  };
}

function getStartTimeColumn(intl: IntlShape, localizationSubset: string): GridColDef<ShiftTypePresetListItem> {
  return {
    field: 'startTime',
    headerName: intl.formatMessage({ id: `${localizationSubset}.table.fields.startTime.heading` }),
    minWidth: 160,
    resizable: true,
    sortable: true,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      if (!params.value) {
        return null;
      }
      return dayjs(params.value, 'HH:mm:ss').format('hh:mm A');
    },
  };
}

function getTypeColumn(intl: IntlShape, localizationSubset: string, theme: Theme): GridColDef<ShiftTypePresetListItem> {
  return {
    field: 'type',
    headerName: intl.formatMessage({ id: `${localizationSubset}.table.fields.type.heading` }),
    minWidth: 160,
    resizable: true,
    sortable: true,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      const row = params.row as ShiftTypePresetListItem;
      const shiftType = getShiftType(dayjs(row.startTime, 'HH:mm:ss'));
      return (
        <Chip
          label={
            <Stack direction={'row'} spacing={1}>
              <ShiftTypeIndicator bgcolor={theme.palette.shiftType[shiftType]} />
              <Typography variant="caption">
                <FormattedMessage id={`${localizationSubset}.table.fields.type.variants.${shiftType}`} />
              </Typography>
            </Stack>
          }
        />
      );
    },
  };
}

const ShiftTypeIndicator = styled(Box)(({ theme }) => ({
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  flexShrink: 0,
  marginTop: `${theme.spacing(0.5)} !important`,
}));

function getDurationColumn(intl: IntlShape, localizationSubset: string): GridColDef<ShiftTypePresetListItem> {
  return {
    field: 'durationInSeconds',
    headerName: intl.formatMessage({ id: `${localizationSubset}.table.fields.durationInSeconds.heading` }),
    minWidth: 160,
    resizable: true,
    display: 'flex',
    flex: 1,
    sortable: true,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      if (!params.value) {
        return null;
      }
      const row = params.row as ShiftTypePresetListItem;
      return (
        <Stack direction={'column'} sx={{ height: '100%', width: '100%' }} justifyContent={'center'} alignContent={'center'}>
          <Stack direction={'row'} spacing={1} alignItems={'center'}>
            <Chip label={dayjs.duration(row.durationInSeconds, 'seconds').humanize()} />
            <Box flex={1} />
            <FaIcon name="angle-right" />
          </Stack>
        </Stack>
      );
    },
  };
}

